import React, { useState } from 'react';

import {
    Typography,
    Grid,
    Box,
    ListItemText,
    ListItemAvatar,
    ListItem,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
} from '@mui/material';
import { PDialogContent } from '@apigrowfinance/pbolt-dialog';
import Icon from '@apigrowfinance/pbolt-icon';
import { InactiveAddress } from '@apigrowfinance/pbolt-icons';
import { Restricted } from '@apigrowfinance/pbolt-permission-provider';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiMore2Line, RiMapPinLine, RiEditBoxLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { AddressDetails } from '@clients/accountsClient';
import { FormatAddress } from '@utils/addressFormatter.ts';
import getEnumTranslation from '@utils/getEnumTranslation';

interface LocationSelectionFormProps {
    handleDeleteLocation: () => void;
    updateSelectedAddress: (addressId: string) => void;
    locations: AddressDetails[];
}

const LocationSelectionList = ({
    handleDeleteLocation,
    updateSelectedAddress,
    locations,
}: LocationSelectionFormProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openElem, setOpenElem] = useState(null);
    const ITEM_HEIGHT = 48;

    const handleClick =
        (elem: number) => (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
            setOpenElem(elem);
        };
    const handleClose = () => {
        setAnchorEl(null);
        setOpenElem(null);
    };
    const { theme } = useTheme();

    const handleEditClick = (addressId: number) => {
        setAnchorEl(null);
        updateSelectedAddress(addressId.toString());
    };

    const handleDeleteClick = (addressId: number) => {
        setAnchorEl(null);
        updateSelectedAddress(addressId.toString());
        handleDeleteLocation();
    };

    return (
        <PDialogContent>
            <Grid
                data-testid="location-list-form"
                container
                flexDirection="column"
                justifyContent="space-evenly">
                {!locations?.length && (
                    <Typography>
                        <FormattedMessage
                            id="LocationsSelectionList.noLocations"
                            defaultMessage="No addresses found on account"
                        />
                    </Typography>
                )}
                {locations.map((location) => {
                    const locationTitle = FormatAddress({
                        type: 'title',
                        address: location,
                    });

                    const locationDescriptive = FormatAddress({
                        type: 'descriptive',
                        address: location,
                    });

                    return (
                        <ListItem
                            key={location?.id || 'loader'}
                            className={
                                location.id === openElem ? 'isHovered' : ''
                            }
                            sx={{
                                mr: 0,
                                borderRadius: theme.shape.borderRadius / 6,
                                mb: 1.5,
                                p: 0,
                                '&:hover, &.isHovered': {
                                    backgroundColor: (theme) =>
                                        theme.palette.mode == 'dark'
                                            ? 'rgba(0, 0, 0, 0.3)'
                                            : theme.palette.grey[100],
                                },
                            }}
                            secondaryAction={
                                <>
                                    <IconButton
                                        id="options-button"
                                        onClick={handleClick(location.id)}>
                                        <RiMore2Line />
                                    </IconButton>
                                    <Menu
                                        id="options-menu"
                                        anchorEl={anchorEl}
                                        open={openElem === location.id}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}>
                                        <Restricted to="edit.location">
                                            <MenuItem
                                                key="Edit"
                                                onClick={() =>
                                                    handleEditClick(location.id)
                                                }
                                                sx={{
                                                    height: ITEM_HEIGHT,
                                                }}>
                                                <ListItemIcon>
                                                    <RiEditBoxLine
                                                        style={{
                                                            fontSize:
                                                                'overline',
                                                            color: theme.palette
                                                                .text.primary,
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <Typography
                                                    variant="overline"
                                                    sx={{
                                                        ml: -1.5,
                                                        color: theme.palette
                                                            .text.primary,
                                                    }}>
                                                    {getEnumTranslation('edit')}
                                                </Typography>
                                            </MenuItem>
                                        </Restricted>
                                        <Restricted to="delete.location">
                                            <MenuItem
                                                key="setInactive"
                                                onClick={() =>
                                                    handleDeleteClick(
                                                        location.id,
                                                    )
                                                }
                                                sx={{
                                                    height: ITEM_HEIGHT,
                                                    mb: 0.5,
                                                }}>
                                                <ListItemIcon>
                                                    <InactiveAddress
                                                        style={{
                                                            width: theme.spacing(
                                                                2,
                                                            ),
                                                            fontSize:
                                                                'overline',
                                                            color: theme.palette
                                                                .error.dark,
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <Typography
                                                    variant="overline"
                                                    sx={{
                                                        ml: -1.5,
                                                        color: theme.palette
                                                            .error.dark,
                                                    }}>
                                                    <FormattedMessage
                                                        id="LocationsSelectionList.markInactive"
                                                        defaultMessage="Mark inactive"
                                                    />
                                                </Typography>
                                            </MenuItem>
                                        </Restricted>
                                    </Menu>
                                </>
                            }>
                            <ListItemAvatar>
                                <Icon data-testid="avatar-icon">
                                    <RiMapPinLine />
                                </Icon>
                            </ListItemAvatar>
                            <Box display="flex">
                                <Box>
                                    <ListItemText
                                        sx={{
                                            alignContent: 'flex-start',
                                            maxWidth: '89%',
                                        }}
                                        primary={
                                            <Typography
                                                variant="h6"
                                                component="h2"
                                                sx={{
                                                    color:
                                                        theme?.custom?.brand
                                                            ?.textMain ||
                                                        'text.primary',
                                                }}>
                                                {locationTitle}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text
                                                        .primary,
                                                }}>
                                                {locationDescriptive}
                                            </Typography>
                                        }
                                    />
                                    {!location?.active && (
                                        <Tag
                                            icon={
                                                <InactiveAddress
                                                    width={theme.spacing(0.375)}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    color="error.dark"
                                                    fontWeight="normal">
                                                    {getEnumTranslation(
                                                        'inactive',
                                                    )}
                                                </Typography>
                                            }
                                            filled
                                        />
                                    )}
                                </Box>
                            </Box>
                        </ListItem>
                    );
                })}
            </Grid>
        </PDialogContent>
    );
};

export default LocationSelectionList;
